// Admin BackOffice 역할 및 권한 정의

/**
 * 관리자 역할 enum
 */
export enum AdminRole {
  /** 시스템 전체 관리 - 모든 권한 보유 */
  SUPER_ADMIN = "SUPER_ADMIN",
  /** 일반 관리자 - 제한된 권한 */
  ADMIN = "ADMIN",
}

/**
 * 관리자 권한 enum
 */
export enum AdminPermission {
  // 사용자 관리
  USER_READ = "user:read",
  USER_CREATE = "user:create",
  USER_UPDATE = "user:update",
  USER_DELETE = "user:delete",

  // 회사 관리
  COMPANY_READ = "company:read",
  COMPANY_CREATE = "company:create",
  COMPANY_UPDATE = "company:update",
  COMPANY_DELETE = "company:delete",

  // 프로젝트 관리
  PROJECT_READ = "project:read",
  PROJECT_UPDATE = "project:update",
  PROJECT_DELETE = "project:delete",
  PROJECT_BATCH = "project:batch", // 일괄 작업

  // 설비 템플릿
  EQUIPMENT_READ = "equipment:read",
  EQUIPMENT_CREATE = "equipment:create",
  EQUIPMENT_UPDATE = "equipment:update",
  EQUIPMENT_DELETE = "equipment:delete",

  // 시스템 설정
  SYSTEM_READ = "system:read",
  SYSTEM_UPDATE = "system:update",

  // 감사 로그
  AUDIT_READ = "audit:read",
}

/**
 * 역할별 기본 권한 매핑
 */
export const ROLE_PERMISSIONS: Record<AdminRole, AdminPermission[]> = {
  [AdminRole.SUPER_ADMIN]: Object.values(AdminPermission), // 모든 권한

  [AdminRole.ADMIN]: [
    AdminPermission.USER_READ,
    AdminPermission.USER_CREATE,
    AdminPermission.USER_UPDATE,
    // USER_DELETE 없음

    AdminPermission.COMPANY_READ,
    AdminPermission.COMPANY_CREATE,
    AdminPermission.COMPANY_UPDATE,
    // COMPANY_DELETE 없음

    AdminPermission.PROJECT_READ,
    AdminPermission.PROJECT_UPDATE,
    // PROJECT_DELETE, PROJECT_BATCH 없음

    AdminPermission.EQUIPMENT_READ,
    AdminPermission.EQUIPMENT_CREATE,
    AdminPermission.EQUIPMENT_UPDATE,
    AdminPermission.EQUIPMENT_DELETE,

    AdminPermission.AUDIT_READ,
    // SYSTEM_* 없음
  ],
};

/**
 * 권한 체크 헬퍼
 * @param userRole 사용자 역할
 * @param userPermissions 사용자에게 개별 부여된 권한 목록
 * @param required 필요한 권한
 * @returns 권한 보유 여부
 */
export function hasPermission(
  userRole: AdminRole,
  userPermissions: string[],
  required: AdminPermission
): boolean {
  // 역할 기본 권한 체크
  if (ROLE_PERMISSIONS[userRole]?.includes(required)) {
    return true;
  }
  // 개별 부여 권한 체크
  return userPermissions.includes(required);
}

/**
 * 사용자가 관리자 역할인지 확인
 * @param role 사용자 역할
 * @returns 관리자 여부
 */
export function isAdminRole(role: string): role is AdminRole {
  return Object.values(AdminRole).includes(role as AdminRole);
}

/**
 * 관리자 역할 목록
 */
export const ADMIN_ROLES = Object.values(AdminRole);

/**
 * 관리자 역할 타입
 */
export type AdminRoleType = `${AdminRole}`;

/**
 * 관리자 권한 타입
 */
export type AdminPermissionType = `${AdminPermission}`;
